import type {EntityOperation} from '@github-ui/optimistic-connection'
import {publishOptimisticFields} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, ConnectionHandler, graphql} from 'react-relay'
import type {Environment, PayloadError, RecordSourceProxy} from 'relay-runtime'

import {BLOCKED_BY_LIST_ALL_RELAY_CONNECTION} from '../components/sections/relations-section/LazyRelationshipsBlockedByListView'
import {BLOCKING_LIST_ALL_RELAY_CONNECTION} from '../components/sections/relations-section/LazyRelationshipsBlockingListView'
import {escapeOptimisticTitleHTML} from '../utils/helpers'
import type {
  AddBlockedByInput,
  addBlockedByMutation,
  addBlockedByMutation$data,
} from './__generated__/addBlockedByMutation.graphql'

export type OptimisticDependencyIssue = {
  id: string
  title: string
  url: string
  number: number
  state: string
  stateReason?: string | null
  repository: {
    id: string
    nameWithOwner: string
  }
}

function optimisticAddBlockedBy(
  store: RecordSourceProxy,
  input: AddBlockedByInput,
  optimisticIssue: OptimisticDependencyIssue,
  direction: 'blockedBy' | 'blocking',
) {
  // Determine which issue record to update based on the direction:
  // - blockedBy: update the issueId's topBlockedBy connection
  // - blocking: update the blockingIssueId's topBlocking connection
  const viewerIssueId = direction === 'blockedBy' ? input.issueId : input.blockingIssueId
  const issueRecord = store.get(viewerIssueId)
  if (!issueRecord) return

  // Get or create the dependency issue record
  let dependencyRecord = store.get(optimisticIssue.id)
  if (!dependencyRecord) {
    dependencyRecord = store.create(optimisticIssue.id, 'Issue')
  }

  // Set scalar fields on the dependency issue
  dependencyRecord.setValue(optimisticIssue.title, 'title')
  // IssuePickerItem only supplies a plaintext title; escape before writing to titleHTML;
  // the server response will overwrite with the real HTML when the mutation resolves.
  dependencyRecord.setValue(escapeOptimisticTitleHTML(optimisticIssue.title), 'titleHTML')
  dependencyRecord.setValue(optimisticIssue.url, 'url')
  dependencyRecord.setValue(optimisticIssue.number, 'number')
  dependencyRecord.setValue(optimisticIssue.state, 'state')
  dependencyRecord.setValue(optimisticIssue.stateReason ?? null, 'stateReason')

  // Set repository on the dependency issue
  let repoRecord = store.get(optimisticIssue.repository.id)
  if (!repoRecord) {
    repoRecord = store.create(optimisticIssue.repository.id, 'Repository')
  }
  repoRecord.setValue(optimisticIssue.repository.nameWithOwner, 'nameWithOwner')
  const [owner, name] = optimisticIssue.repository.nameWithOwner.split('/')
  repoRecord.setValue(name, 'name')

  let ownerRecord = repoRecord.getLinkedRecord('owner')
  if (!ownerRecord) {
    ownerRecord = store.create(`${optimisticIssue.repository.id}:owner:optimistic`, 'Organization')
    repoRecord.setLinkedRecord(ownerRecord, 'owner')
  }
  ownerRecord.setValue(owner, 'login')

  dependencyRecord.setLinkedRecord(repoRecord, 'repository')

  // Append the dependency issue to the appropriate connection
  const connectionField = direction === 'blockedBy' ? 'blockedBy' : 'blocking'
  const connection = issueRecord.getLinkedRecord(connectionField, {first: 3, ranked: true})
  if (connection) {
    const existingNodes = connection.getLinkedRecords('nodes') || []
    if (!existingNodes.some(node => node?.getDataID() === optimisticIssue.id)) {
      connection.setLinkedRecords([...existingNodes, dependencyRecord], 'nodes')
    }
  }

  // Update issueDependenciesSummary
  const summary = issueRecord.getLinkedRecord('issueDependenciesSummary')
  if (summary) {
    const currentCount = (summary.getValue(connectionField) as number) || 0
    summary.setValue(currentCount + 1, connectionField)
  }
}

export function addBlockedByMutation({
  environment,
  input,
  onError,
  onCompleted,
  optimisticBlockingIssue,
  optimisticDirection,
}: {
  environment: Environment
  input: AddBlockedByInput
  onError?: (error: Error) => void
  onCompleted?: (response: addBlockedByMutation$data, errors?: readonly PayloadError[] | null) => void
  optimisticBlockingIssue?: OptimisticDependencyIssue
  optimisticDirection?: 'blockedBy' | 'blocking'
}) {
  const countOperation: EntityOperation | undefined =
    optimisticDirection === 'blockedBy'
      ? {
          type: 'deltaEntityField',
          id: input.issueId,
          field: 'blockedBy',
          delta: 1,
          clamp: {min: 0},
        }
      : undefined
  const publication = countOperation ? publishOptimisticFields(countOperation) : undefined

  try {
    return commitMutation<addBlockedByMutation>(environment, {
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      mutation: graphql`
        mutation addBlockedByMutation($input: AddBlockedByInput!) @raw_response_type {
          addBlockedBy(input: $input) {
            issue {
              # Inlined from the former RelationshipsSectionFragment to keep the Relay store fresh.
              id
              databaseId
              viewerCanUpdateMetadata
              repository {
                nameWithOwner
                isArchived
                owner {
                  login
                }
              }
              parent {
                id
                titleHTML
                url
                number
                repository {
                  nameWithOwner
                }
                state
                stateReason
                subIssuesSummary {
                  total
                  completed
                }
              }
              topBlockedBy: blockedBy(first: 3, ranked: true) {
                nodes {
                  id
                  title
                  titleHTML
                  url
                  number
                  repository {
                    nameWithOwner
                    name
                    owner {
                      login
                    }
                  }
                  state
                  stateReason
                }
                pageInfo {
                  hasNextPage
                }
              }
              topBlocking: blocking(first: 3, ranked: true) {
                nodes {
                  id
                  title
                  titleHTML
                  url
                  number
                  repository {
                    nameWithOwner
                    name
                    owner {
                      login
                    }
                  }
                  state
                  stateReason
                }
                pageInfo {
                  hasNextPage
                }
              }
              securityAlerts(first: 100) {
                nodes {
                  number
                  repository {
                    nameWithOwner
                  }
                  status
                  title
                }
              }
              issueDependenciesSummary {
                blockedBy
                blocking
              }
            }
            blockingIssue {
              id
              number
              title
              # Inlined from the former RelationshipsSectionFragment to keep the Relay store fresh.
              databaseId
              viewerCanUpdateMetadata
              repository {
                nameWithOwner
                isArchived
                owner {
                  login
                }
              }
              parent {
                id
                titleHTML
                url
                number
                repository {
                  nameWithOwner
                }
                state
                stateReason
                subIssuesSummary {
                  total
                  completed
                }
              }
              topBlockedBy: blockedBy(first: 3, ranked: true) {
                nodes {
                  id
                  title
                  titleHTML
                  url
                  number
                  repository {
                    nameWithOwner
                    name
                    owner {
                      login
                    }
                  }
                  state
                  stateReason
                }
                pageInfo {
                  hasNextPage
                }
              }
              topBlocking: blocking(first: 3, ranked: true) {
                nodes {
                  id
                  title
                  titleHTML
                  url
                  number
                  repository {
                    nameWithOwner
                    name
                    owner {
                      login
                    }
                  }
                  state
                  stateReason
                }
                pageInfo {
                  hasNextPage
                }
              }
              securityAlerts(first: 100) {
                nodes {
                  number
                  repository {
                    nameWithOwner
                  }
                  status
                  title
                }
              }
              issueDependenciesSummary {
                blockedBy
                blocking
              }
            }
          }
        }
      `,
      variables: {
        input,
      },
      optimisticUpdater:
        optimisticBlockingIssue && optimisticDirection
          ? store => optimisticAddBlockedBy(store, input, optimisticBlockingIssue, optimisticDirection)
          : undefined,
      onError: error => {
        publication?.failed(error)
        if (onError) onError(error)
      },
      onCompleted: (response, errors) => {
        const blockedByCount = response.addBlockedBy?.issue?.issueDependenciesSummary?.blockedBy
        if (countOperation && typeof blockedByCount === 'number') {
          publication?.completed({type: 'setFields', id: input.issueId, fields: {blockedBy: blockedByCount}})
        } else if (!response.addBlockedBy || countOperation) {
          publication?.failed(new Error(errors?.[0]?.message ?? 'Failed to add dependency'))
        }
        if (onCompleted) onCompleted(response, errors)
      },
      onUnsubscribe: () => publication?.failed(new Error('Adding dependency was cancelled')),
      updater: store => {
        store.get(input.issueId)?.getLinkedRecord('blockedBy', {first: 100})?.invalidateRecord()
        store.get(input.blockingIssueId)?.getLinkedRecord('blocking', {first: 100})?.invalidateRecord()

        const blockedByDataId = ConnectionHandler.getConnectionID(input.issueId, BLOCKED_BY_LIST_ALL_RELAY_CONNECTION)
        store.get(blockedByDataId)?.invalidateRecord()

        const blockingDataId = ConnectionHandler.getConnectionID(
          input.blockingIssueId,
          BLOCKING_LIST_ALL_RELAY_CONNECTION,
        )
        store.get(blockingDataId)?.invalidateRecord()
      },
    })
  } catch (error) {
    publication?.failed(error instanceof Error ? error : new Error('Failed to add dependency'))
    throw error
  }
}
